import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { setPageMeta } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CursoLandingPage() {
  const { slug } = useParams();
  const [curso, setCurso] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/curso/${slug}`)
      .then((r) => {
        setCurso(r.data);
        setPageMeta({
          title: `${r.data.title} | Curso SENAR | HeveaTalent`,
          description: `Fez o curso ${r.data.title}? Cadastre-se grátis no Balcão de Empregos da HeveaTalent e seja encontrado pelas fazendas.`,
          canonical: `https://heveatalent.com.br/curso/${slug}`,
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-6 text-center" data-testid="curso-not-found">
        <div>
          <p className="text-4xl">🎓</p>
          <p className="mt-2 font-bold text-gray-900">Curso não encontrado</p>
          <Link to="/cursos" className="mt-3 inline-block text-green-700 underline text-sm">Ver todos os cursos →</Link>
        </div>
      </div>
    );
  }

  if (!curso) {
    return <div className="min-h-screen bg-green-50 flex items-center justify-center text-gray-500 text-sm">Carregando…</div>;
  }

  const isEad = curso.kind === "ead";
  const cadastroLink = `/?cadastro=vaga&curso=${encodeURIComponent(slug)}`;

  return (
    <div className="min-h-screen bg-green-50" data-testid="curso-landing-page">
      <header className="bg-green-900 text-white py-4 px-4 text-center">
        <Link to="/" className="font-bold text-lg">🌳 HeveaTalent</Link>
        <p className="text-green-200 text-xs">Balcão de Empregos da Heveicultura — gratuito</p>
      </header>

      <main className="max-w-md mx-auto p-4 pb-10">
        <Card className="border-2 border-green-600">
          <CardContent className="pt-5">
            <Badge className="bg-green-700 text-white text-[10px]">{isEad ? "CURSO EAD SENAR — GRÁTIS" : "CURSO SENAR"}</Badge>
            <h1 className="text-xl font-bold text-gray-900 mt-2" data-testid="curso-title">{curso.title}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {isEad
                ? `💻 Online · ${curso.carga} · até ${curso.duracao}`
                : `📍 ${curso.city}${curso.uf ? `/${curso.uf}` : ""}${curso.dates_label ? ` · 📅 ${curso.dates_label}` : ""}`}
            </p>
            {(curso.desc || curso.conteudo) && (
              <p className="text-sm text-gray-700 mt-2">{curso.desc || curso.conteudo}</p>
            )}
          </CardContent>
        </Card>

        <div className="mt-5 bg-white border-2 border-amber-400 rounded-2xl p-5 text-center" data-testid="curso-cta-card">
          {isEad ? (
            <>
              <p className="text-2xl">💻</p>
              <p className="font-bold text-gray-900 mt-1">Ainda não fez? É grátis e online:</p>
              <a
                href={curso.url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 block w-full text-center bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 rounded-xl text-sm"
                data-testid="curso-ead-link"
              >
                Fazer o curso no EAD SENAR →
              </a>
              <div className="border-t mt-4 pt-4">
                <p className="font-bold text-gray-900">🎉 Já concluiu o curso?</p>
                <p className="text-sm text-gray-600 mt-1">
                  Cadastre-se grátis no Balcão de Empregos e apareça para as fazendas com o selo 🎓 de aluno SENAR.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-2xl">🎉</p>
              <p className="font-bold text-gray-900 mt-1">
                {curso.finished ? "Parabéns, turma formada!" : "Fez (ou está fazendo) este curso?"}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Cadastre-se grátis no Balcão de Empregos da HeveaTalent e apareça para as fazendas da região com o selo 🎓 de aluno do curso.
              </p>
            </>
          )}
          <a
            href={cadastroLink}
            className="mt-4 block w-full text-center bg-green-700 hover:bg-green-800 text-white font-bold py-4 rounded-xl"
            data-testid="curso-register-btn"
          >
            🎓 Me cadastrar como aluno do curso →
          </a>
          <p className="text-xs text-gray-500 mt-2">Leva 1 minuto · grátis · seu perfil já entra como "disponível para trabalho"</p>
        </div>

        <div className="mt-5 bg-white border border-green-200 rounded-2xl p-5" data-testid="curso-benefits">
          <p className="font-bold text-green-900 text-sm">O que você ganha ao se cadastrar:</p>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
            <li>🤝 Aparece na vitrine de <b>Profissionais</b> para quem contrata</li>
            <li>💼 Acesso às <b>vagas de sangria</b> de toda a região</li>
            <li>📰 Notícias, <b>preços da borracha</b> e dicas da comunidade</li>
          </ul>
          <Link to="/balcao" className="mt-3 block text-center text-green-700 text-xs underline" data-testid="curso-balcao-link">
            Ver vagas abertas no Balcão →
          </Link>
        </div>
      </main>
    </div>
  );
}
